import React, { useState, useMemo } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { Button, Empty, Spin, Select, DatePicker } from 'antd-v5';
import { HistoryOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import StandardLayoutV2 from '../../../Layout/StandardLayoutV2';
import EventCard from './components/EventCard';
import EventLinksModal from './components/EventLinksModal';
import { useEvents } from '../../../../hooks/query/useEvents';
import { useMerchantStores } from '../../../../hooks/query/useStores';
import './styles.scss';

// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { RangePicker } = DatePicker;

// Helper to check if event is currently in progress
const isEventInProgress = event => {
  const now = dayjs();
  const startDate = event.startDate;
  const endDate = event.endDate;

  if (!startDate || !endDate) return false;

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return start.isSameOrBefore(now, 'day') && end.isSameOrAfter(now, 'day');
};

const Events = () => {
  const history = useHistory();
  const { events: eventsFlag } = useFlags();

  // Redirect if feature flag is disabled
  if (!eventsFlag) {
    return <Redirect to="/" />;
  }

  // Filter state
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  // Event Links Modal state
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Pagination state (Load More)
  const [currentEventsLimit, setCurrentEventsLimit] = useState(8);

  // Fetch stores for filter dropdown
  const { data: storesData } = useMerchantStores({
    useQueryOptions: {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes to reduce refetching
    },
  });
  const stores = storesData?.results || storesData?.data || [];

  // Build query params for API
  const queryParams = useMemo(() => {
    const params = {
      offset: 0,
      limit: 100, // API max is 100, pagination handled client-side
    };
    if (selectedStoreIds.length > 0) {
      // API expects comma-separated string, not array
      params.storeIds = selectedStoreIds.join(',');
    }
    if (dateRange[0]) {
      params.fromDate = dateRange[0].format('YYYY-MM-DD');
    }
    if (dateRange[1]) {
      params.toDate = dateRange[1].format('YYYY-MM-DD');
    }
    return params;
  }, [selectedStoreIds, dateRange]);

  // Fetch events with filters
  const {
    data: eventsData,
    isLoading,
    error,
  } = useEvents({
    query: queryParams,
    useQueryOptions: {
      staleTime: 30 * 1000, // Cache for 30 seconds
    },
  });

  const events = eventsData?.data || eventsData || [];

  // Split events into current (in progress + upcoming) and past
  // Track which events are in progress for badge display
  const { currentEvents, inProgressEventIds } = useMemo(() => {
    const now = dayjs();
    const current = [];
    const inProgressIds = new Set();

    events.forEach(event => {
      const eventEndDate = event.endDate;

      // Check if event is in progress (started but not ended)
      if (isEventInProgress(event)) {
        current.push(event);
        inProgressIds.add(event.id);
      } else if (eventEndDate && dayjs(eventEndDate).isBefore(now, 'day')) {
        // Past: end date is before today - skip, these go to past events page
      } else {
        // Upcoming or events without proper dates
        current.push(event);
      }
    });

    // Sort by start date (ascending) - earliest first
    const sortByStartDate = (a, b) => {
      const startA = a.startDate;
      const startB = b.startDate;

      // Events without dates go to the end
      if (!startA && !startB) return 0;
      if (!startA) return 1;
      if (!startB) return -1;

      return dayjs(startA).diff(dayjs(startB));
    };

    return {
      currentEvents: current.sort(sortByStartDate),
      inProgressEventIds: inProgressIds,
    };
  }, [events]);

  // Paginated events for display
  const displayedCurrentEvents = currentEvents.slice(0, currentEventsLimit);
  const hasMoreCurrentEvents = currentEvents.length > currentEventsLimit;

  const handleAddEvent = () => {
    history.push('/Events/add-event');
  };

  const handleEventClick = eventId => {
    history.push(`/Events/event-details/${eventId}`);
  };

  const handleLinksClick = event => {
    setSelectedEvent(event);
    setShowLinksModal(true);
  };

  if (error) {
    return (
      <StandardLayoutV2
        title="Events"
        classProps="events-page"
        containerStyles={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px' }}
      >
        <div className="events-error">
          <Empty description="Failed to load events. Please try again." />
        </div>
      </StandardLayoutV2>
    );
  }

  return (
    <StandardLayoutV2
      title="Events"
      classProps="events-page"
      containerStyles={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px' }}
    >
      {/* Header Actions */}
      <div className="events-header flex-row-spacebetween-nowrap m-btm-24">
        <div className="flex-row-nospacebetween-nowrap gap-16">
          <Select
            mode="multiple"
            allowClear
            placeholder="Filter by Store"
            value={selectedStoreIds}
            onChange={setSelectedStoreIds}
            style={{ minWidth: 180 }}
            maxTagCount={1}
            maxTagPlaceholder={omittedValues => `+${omittedValues.length} more`}
          >
            {stores.map(store => (
              <Select.Option key={store.id} value={store.id}>
                {store.name}
              </Select.Option>
            ))}
          </Select>
          <RangePicker
            value={dateRange}
            onChange={dates => setDateRange(dates || [null, null])}
            placeholder={['Start Date', 'End Date']}
            allowClear
          />
        </div>
        <div className="flex-row-nospacebetween-nowrap gap-16">
          <Button icon={<HistoryOutlined />} onClick={() => history.push('/Events/past')}>
            View Past Events
          </Button>
          <Button type="primary" onClick={handleAddEvent}>
            + Add Event
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="events-loading flex-col-center">
          <Spin size="large" />
        </div>
      ) : (
        <div className="events-section">
          {currentEvents.length > 0 ? (
            <>
              <div className="events-grid">
                {displayedCurrentEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    isInProgress={inProgressEventIds.has(event.id)}
                    onClick={() => handleEventClick(event.id)}
                    onLinksClick={handleLinksClick}
                  />
                ))}
              </div>
              {hasMoreCurrentEvents && (
                <div className="events-load-more m-top-24">
                  <Button onClick={() => setCurrentEventsLimit(prev => prev + 8)}>
                    Load More ({currentEvents.length - currentEventsLimit} remaining)
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Empty description="No events" />
          )}
        </div>
      )}

      {/* Event Links Modal */}
      <EventLinksModal open={showLinksModal} onClose={() => setShowLinksModal(false)} event={selectedEvent} />
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(Events);
